import React, { useEffect, useState } from "react";
import { Bell, AlertTriangle, FileCheck } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [user]);

  if (user?.role !== "admin") return null;

  const unread = notes.filter((n) => !n.read).length;

  const handleClick = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    if (n.indent_id) navigate(`/indents?focus=${n.indent_id}`);
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-slate-100"
          data-testid="notifications-trigger"
        >
          <Bell size={18} className="text-slate-600" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center"
            >
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Notifications</div>
            <div className="text-[11px] text-slate-500">High priority shown first</div>
          </div>
          <span className="text-xs text-slate-500">{unread} unread</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notes.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications</div>
          )}
          {notes.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-${n.id}`}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                n.read ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                {n.priority === "high" ? (
                  <FileCheck size={16} className="text-rose-600 mt-0.5" />
                ) : (
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {n.title}
                    </span>
                    <span
                      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                        n.priority === "high"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {n.priority}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                  <div className="text-[10px] text-slate-400 mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
